/**
 * Subscription Management Component
 *
 * Button states:
 *  1. Real Stripe sub_* id  → calls create-portal-session → redirects to Stripe
 *  2. localhost dev unlock  → shows inline info (no email, no alert)
 *  3. Production, no sub_*  → shows inline email fallback
 */

import React, { useState, useEffect } from 'react';
import { SubscriptionManager } from '../SubscriptionManager.js';
import { openPricingModal } from '../utils/openPricingModal.js';

function getSubId() {
  return (localStorage.getItem('subscription_id') || '').trim();
}

function isLocalDev() {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1';
}

export const SubscriptionManagement = ({ onClose }) => {
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState(null);

  useEffect(() => {
    try {
      setSubscriptionInfo(SubscriptionManager.getSubscriptionInfo());
    } catch (_) {
      setSubscriptionInfo(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleManageSubscription = async () => {
    setPortalError(null);
    const subId = getSubId();

    // ── localhost dev unlock ──────────────────────────────────────────────────
    if (isLocalDev() && !subId.startsWith('sub_')) {
      setPortalError('__localhost__');
      return;
    }

    // ── no real Stripe id on production — try email lookup ───────────────────
    if (!subId.startsWith('sub_') && !subId.startsWith('cs_')) {
      const email = (localStorage.getItem('subscription_email') || '').trim();
      if (email) {
        setPortalLoading(true);
        try {
          const lookupRes = await fetch('/.netlify/functions/check-subscription', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
          });
          if (lookupRes.ok) {
            const data = await lookupRes.json();
            if (data.subscriptionId && data.subscriptionId.startsWith('sub_')) {
              localStorage.setItem('subscription_id', data.subscriptionId);
              // Re-call with the now-correct ID
              setPortalLoading(false);
              void handleManageSubscription();
              return;
            }
          }
        } catch (_) {}
        setPortalLoading(false);
      }
      setPortalError('__no_id__');
      return;
    }

    // ── real Stripe subscription ──────────────────────────────────────────────
    setPortalLoading(true);
    try {
      const res = await fetch('/.netlify/functions/create-portal-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subscriptionId: subId,
          returnUrl: `${window.location.origin}/app.html`,
        }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const { url } = await res.json();
      window.location.href = url;
    } catch (err) {
      console.error('[portal]', err);
      setPortalError('__fetch_failed__');
    } finally {
      setPortalLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
        </div>
      </div>
    );
  }

  const hasSubscription = subscriptionInfo?.isPro || subscriptionInfo?.hasSubscription;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-lg">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold mb-1">Subscription</h2>
              <p className="text-blue-100 text-sm">Billing, payment method and cancellation</p>
            </div>
            <button onClick={onClose} className="text-white hover:text-gray-200 text-3xl leading-none">
              ×
            </button>
          </div>
        </div>

        <div className="p-6">
          {hasSubscription ? (
            <>
              {/* Plan card */}
              <div className="bg-green-50 border-2 border-green-200 rounded-lg p-5 mb-5">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">
                      {subscriptionInfo.tier === 'premium' ? 'All Access' : 'Advanced Retirement'}
                    </h3>
                    <p className="text-sm text-gray-600 mt-0.5">
                      {subscriptionInfo.isOnTrial ? 'Trial period' : 'Active subscription'}
                    </p>
                    {subscriptionInfo.renewalDate && (
                      <p className="text-xs text-gray-500 mt-1">
                        Next billing: {new Date(subscriptionInfo.renewalDate).toLocaleDateString('en-AU')}
                      </p>
                    )}
                  </div>
                  <span className="px-3 py-1.5 bg-green-600 text-white rounded-full text-sm font-semibold">
                    Active
                  </span>
                </div>
              </div>

              {/* Portal button */}
              <div className="mb-5">
                <button
                  onClick={handleManageSubscription}
                  disabled={portalLoading}
                  className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition shadow disabled:opacity-60 disabled:cursor-wait"
                >
                  {portalLoading ? 'Opening Stripe…' : 'Manage Subscription'}
                </button>
                <p className="text-xs text-gray-400 mt-2 text-center">
                  Opens Stripe — update card, download invoices, or cancel (takes effect at period end)
                </p>

                {/* Inline error states — no alerts, no email popups */}
                {portalError === '__localhost__' && (
                  <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
                    <strong>Localhost dev mode.</strong> The Stripe portal only works on the live site.
                    Locally your subscription ID is a dev placeholder, not a real Stripe ID.
                    On <a href="https://supercalcpro.com.au" className="underline">supercalcpro.com.au</a> this
                    button redirects straight to Stripe so you can cancel or update your card.
                  </div>
                )}

                {portalError === '__no_id__' && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
                    Your Stripe subscription ID wasn't found in this browser. This can happen if you
                    subscribed on a different device or cleared storage. Use{' '}
                    <a href="/restore-access.html" className="underline font-medium">
                      Restore Access
                    </a>{' '}
                    with your checkout email. If that fails, email{' '}
                    <a href="mailto:support@supercalcpro.com.au" className="underline font-medium">
                      support@supercalcpro.com.au
                    </a>{' '}
                    and we'll fix it.
                  </div>
                )}

                {portalError === '__fetch_failed__' && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
                    Couldn't open the Stripe portal right now. Try refreshing, or email{' '}
                    <a
                      href="mailto:support@supercalcpro.com.au?subject=Cancel%20my%20subscription"
                      className="underline font-medium"
                    >
                      support@supercalcpro.com.au
                    </a>
                    .
                  </div>
                )}
              </div>

              {/* Features */}
              <div className="bg-gray-50 rounded-lg p-5 mb-5">
                <h4 className="font-semibold text-gray-900 mb-3 text-sm">Your plan includes:</h4>
                <ul className="space-y-1.5 text-sm text-gray-700">
                  {(subscriptionInfo.tier === 'premium'
                    ? ['All calculators (Basic, Accumulation, Advanced, SMSF)', 'Monte Carlo simulations', 'Historical analysis', 'PDF exports', 'SMSF Suite & specialized tools']
                    : ['Advanced Calculator', 'Monte Carlo simulations', 'Historical analysis', 'PDF exports']
                  ).map((f) => (
                    <li key={f} className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </>
          ) : (
            <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-6 mb-5 text-center">
              <div className="text-5xl mb-4">🔒</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">No Active Subscription</h3>
              <p className="text-gray-600 mb-4">You're on Free Calculators. Upgrade to unlock Advanced Retirement or All Access features.</p>
              <button
                onClick={() => {
                  onClose();
                  openPricingModal({ source: 'subscription_management', calculatorTier: 'unknown' });
                }}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition"
              >
                View Plans
              </button>
            </div>
          )}

          {/* Support footer */}
          <div className="border-t pt-4">
            <p className="text-xs text-gray-500">
              Need help?{' '}
              <a href="mailto:support@supercalcpro.com.au" className="text-blue-600 hover:underline">
                support@supercalcpro.com.au
              </a>{' '}
              — we respond same business day.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionManagement;
